/**
 * The acting user's claim-WRITE provider panel — the authorization gate for
 * Create Claim, ported from Provider Portal's `CreateClaimModal`
 * (providerPortalFrontend `frontend/src/components/Claims/CreateClaimModal/index.tsx:100-147`).
 *
 * WHY THIS IS LOAD-BEARING, not cosmetic:
 *
 *   1. AUTHORIZATION — of the SEED, not of the filing. PP-API resolves this list
 *      from the acting user's own PortalManager grants, keeping only providers
 *      whose claim access is "Read Write". An empty list is PP's way of saying
 *      "this user may not create claims", and `GET ClaimMember/CreateClaim` (the
 *      shell seed) does NOT re-check it — its only guard is
 *      `tokenUserObjectId == userId` plus non-blank params. So this list is the
 *      only thing standing between a user and an OPEN, SEEDED claim form, and an
 *      empty list, a failed call, or a missing bearer MUST block, never warn.
 *
 *      It is NOT, however, the last line of defense before a claim is FILED, and
 *      must not be described as such: `ClaimMemberController.SaveClaim`
 *      (providerPortalAPI `origin/dev`, AM-5619) calls
 *      `EnsureEntitledToSubmitClaimAsync` before `_claimService.SaveClaim`, which
 *      re-binds the claim's BILLING provider (`master.RmiProvKeyId`) to the
 *      caller's own PortalManager grants via `GetDistinctProviderKeyListWDates`,
 *      keyed on the bearer's object id (never a peek target), and fails closed on
 *      a missing company id, a missing/malformed prov key, or an empty entitled
 *      list. That server check is the STRONGER of the two — do not treat it as
 *      redundant or relax it because this client-side list exists.
 *
 *      (The persona `useActionAllowed("Claims","submit")` check is a third,
 *      separate gate — a member-360 VISIBILITY policy, not a PP entitlement.)
 *   2. CORRECTNESS. `RmiProvKeyId` is a GUID that PP-API parses with an
 *      unguarded `new Guid(provKeyId)` and then uses to populate the claim's
 *      billing-provider / facility / provider-NPI columns. Empty ⇒ HTTP 400
 *      (`provKeyId` is a non-nullable string on a `<Nullable>enable</Nullable>`
 *      project, so `""` trips the implicit `[Required]` exactly like a missing
 *      param); malformed ⇒ an unhandled parse failure. Only a real GUID from
 *      this list is ever acceptable — and supplying one is a job nothing else
 *      does for us.
 *
 * Endpoint: `GET {ppApiBaseUrl}/ClaimMember/GetClaimReadWriteProviderList
 *            ?userId={ppUserOid}&companyId={ipa}&userRole={role}` →
 * `RmiAuthProviderModel[]`, PascalCase. Returns 403 when `userId` doesn't match
 * the bearer's identity.
 *
 * `userRole` (added for the BE's `SHOW_ALL_PROVIDERS_ROLES` rework — the same
 * source SaveClaim's entitlement gate now draws from) is OPTIONAL BY DESIGN:
 * the BE treats an absent/empty value as "no role expansion", not an error.
 * Never block or throw when a caller can't resolve one — the fallback is
 * simply the narrower, grants-only list this endpoint always returned before.
 * Do NOT "tighten" this into a required param later: a role-less caller (e.g.
 * an ambient/non-PP identity) must still be able to open the gate on their
 * explicit grants alone, not get hard-blocked for lacking a role claim.
 *
 * DEPLOYED to DEV 2026-07-30 (release 212/37701 from build 353/111892) and to QA
 * (212/37702 from 353/111893). Verified live: this path returns 401 through the
 * dev edge exactly like the known-live sibling `ClaimMember/GetVendors`, while a
 * nonsense path under the same prefix returns 404 — so the route is registered
 * end to end. No APIM/per-operation registration was needed; that is a
 * portalManager concern, not this API's.
 *
 * It is NOT mocked in dev by design — it is the Create Claim authorization gate,
 * and a stub answering "yes" has no business running in any environment.
 */

/**
 * One row of PP-API's `RmiAuthProviderModel`, narrowed to the fields this host
 * reads. PascalCase because PP-API serializes this model title-cased (the same
 * convention every other Rmi* payload in this repo uses).
 */
export interface ClaimProvider {
  /** GUID. The value that becomes `provKeyId` on `ClaimMember/CreateClaim`. */
  RmiProvKeyId: string;
  /** The IPA this grant belongs to. Filtered against the acting IPA. */
  RmiCompanyId: string;
  /**
   * Provider class — `"S"` = Specialist. Threaded into the @astrana/ui
   * CreateClaim module as `provider.RmiClass`, where it drives the
   * referring-provider-required-for-specialists validation
   * (`renderingProviderRmiClass` in the module's form schema).
   */
  RmiClass?: string | null;
  /** Address/location discriminator — two rows can share a RmiProvKeyId. */
  RmiAddressId?: string | number | null;
  /** Display label PP's own picker sorts and renders on. */
  Provider?: string | null;
  RmiFullName?: string | null;
  RmiCity?: string | null;
  RmiStreet?: string | null;
  RmiClaimAccess?: string | null;
  RmiClaimAccessDays?: number | null;
}

/**
 * Thrown on any non-OK response, carrying the HTTP `status` so the caller can
 * tell a DENIAL (403 — bearer/userId mismatch or no claim entitlement) and a
 * NOT-YET-DEPLOYED endpoint (404) apart from a transient failure (5xx/network),
 * and word the block accordingly. Every one of them still blocks.
 */
export class ClaimProviderListError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`GetClaimReadWriteProviderList failed: ${status}`);
    this.name = "ClaimProviderListError";
    this.status = status;
  }
}

/**
 * Sentinel `ClaimProviderListError.status` for a response that arrived with a
 * perfectly fine HTTP code but a body this client cannot use (a 200 whose
 * payload isn't a list). There is no meaningful HTTP status to show the user
 * there — reporting the real one produces the nonsense copy "Couldn't verify
 * your claim access with Provider Portal (HTTP 200)".
 *
 * Deliberately `0`, i.e. FALSY, so every `httpStatus ? …` guard in the UI and in
 * telemetry suppresses it with no special-casing.
 */
export const CLAIM_PROVIDER_LIST_NO_HTTP_STATUS = 0;

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A `RmiProvKeyId` is only usable if PP-API's `new Guid(provKeyId)` will parse
 * it AND it isn't the all-zero placeholder. Rows failing this are dropped
 * rather than offered: selecting one could only produce a 400/500, and the
 * all-zero GUID is separately known to make the BE hang (see
 * auth/ppEligibility.ts's `NO_PROVIDER_FILTER` note).
 */
export function isUsableProvKeyId(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return GUID_RE.test(v) && v.toLowerCase() !== EMPTY_GUID;
}

/** Normalizes one raw JSON row; returns null when it can't be used. */
function toClaimProvider(raw: unknown): ClaimProvider | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!isUsableProvKeyId(r.RmiProvKeyId)) return null;
  const companyId = typeof r.RmiCompanyId === "string" ? r.RmiCompanyId : "";
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return {
    RmiProvKeyId: (r.RmiProvKeyId as string).trim(),
    RmiCompanyId: companyId,
    RmiClass: str(r.RmiClass),
    RmiAddressId:
      typeof r.RmiAddressId === "string" || typeof r.RmiAddressId === "number"
        ? r.RmiAddressId
        : null,
    Provider: str(r.Provider),
    RmiFullName: str(r.RmiFullName),
    RmiCity: str(r.RmiCity),
    RmiStreet: str(r.RmiStreet),
    RmiClaimAccess: str(r.RmiClaimAccess),
    RmiClaimAccessDays: typeof r.RmiClaimAccessDays === "number" ? r.RmiClaimAccessDays : null,
  };
}

export interface FetchClaimProviderListArgs {
  /** PP API base URL INCLUDING the `/api` suffix (as the module's prop has). */
  ppApiBaseUrl: string;
  /** BARE B2C bearer — no "Bearer " scheme prefix (see below). */
  bearer: string;
  /** The PP user id decoded from THAT SAME bearer. */
  userId: string;
  /** Acting IPA — scopes the grant lookup and picks the PP-API DB connection. */
  companyId: string;
  /**
   * Drives the BE's `SHOW_ALL_PROVIDERS_ROLES` expansion (SUPER ADMIN / QCIT /
   * ADMINISTRATOR / CASE, etc. see providers beyond their explicit grants).
   *
   * DELIBERATELY OPTIONAL — the BE accepts it absent/empty and just skips the
   * expansion; a caller with no resolvable role must still get its explicit
   * grants back, not a hard block. See the header note above before making
   * this required.
   */
  userRole?: string;
  /**
   * Aborts the in-flight request when the caller goes away (the gate closed, the
   * member changed, a retry superseded this attempt). Without it a slow PP-API
   * call keeps a socket and a pending promise alive past the gate's lifetime;
   * with it the request is cancelled and `fetch` rejects with an `AbortError`,
   * which callers must treat as "cancelled", NOT as a failure to report.
   */
  signal?: AbortSignal;
}

/**
 * Fetches the acting user's claim-write provider list. Rejects (never returns
 * a partial/empty "success") on any non-OK response so callers cannot mistake
 * a failed call for "you have no providers".
 *
 * Header convention is the repo's existing one for direct PP-API calls —
 * see auth/ppEligibility.ts: a BARE token (no "Bearer " prefix; PP's
 * `JwtSecurityTokenHandler.ReadJwtToken` rejects the prefix outright) plus
 * `X-Backend-Id` for tenant routing.
 */
export async function fetchClaimReadWriteProviderList({
  ppApiBaseUrl,
  bearer,
  userId,
  companyId,
  userRole,
  signal,
}: FetchClaimProviderListArgs): Promise<ClaimProvider[]> {
  const params = new URLSearchParams({ userId, companyId });
  // OMITTED, not sent empty, when unresolvable: the BE treats absent/empty the
  // same (no role expansion), but omitting entirely also sidesteps ever
  // building the literal query string `userRole=undefined` from a careless
  // `String(userRole)` upstream, and keeps the URL identical to this
  // endpoint's pre-rework shape for every caller that has no role to offer.
  if (userRole) params.set("userRole", userRole);
  const res = await fetch(
    `${ppApiBaseUrl}/ClaimMember/GetClaimReadWriteProviderList?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: bearer,
        "X-Backend-Id": companyId,
      },
      signal,
    },
  );

  if (!res.ok) throw new ClaimProviderListError(res.status);

  const body: unknown = await res.json().catch(() => null);
  const rows = Array.isArray(body)
    ? body
    : Array.isArray((body as { value?: unknown[] } | null)?.value)
      ? (body as { value: unknown[] }).value
      : null;
  // A 200 whose body isn't a list is not "no providers" — it's an unusable
  // response, and must block like any other failure. The status carried is the
  // no-HTTP-status sentinel, NOT `res.status`: the transport was fine, so
  // quoting "HTTP 200" at the user would be actively misleading.
  if (rows === null) throw new ClaimProviderListError(CLAIM_PROVIDER_LIST_NO_HTTP_STATUS);

  return rows.map(toClaimProvider).filter((p): p is ClaimProvider => p !== null);
}

/** Case/whitespace-insensitive IPA comparison (PP-API returns CHAR columns). */
function sameIpa(a: string, b: string): boolean {
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

/** The label PP's picker sorts and displays on (`Provider`, falling back). */
export function claimProviderLabel(p: ClaimProvider): string {
  return (p.Provider ?? p.RmiFullName ?? "").trim() || p.RmiProvKeyId;
}

/** Secondary line: the practice address, as PP's own picker renders it. */
export function claimProviderAddress(p: ClaimProvider): string {
  return [p.RmiStreet, p.RmiCity]
    .map((v) => (v ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * Legacy parity for `filter(p => p.RmiCompanyId === ipaGroup)` +
 * `sortByIPA(filtered, "Provider")` (CreateClaimModal:107-111).
 *
 * `sortByIPA` groups rows by `RmiCompanyId` and floats the acting IPA's group
 * first — after the filter above, only ONE IPA remains, so it degenerates to
 * its inner `sortArrayByKey(list, "Provider")`. Implemented as that sort
 * directly rather than reproducing the now-dead grouping.
 *
 * The IPA comparison is case/whitespace-insensitive where legacy's was a strict
 * `===`. That can only ever ADMIT a row legacy would have dropped on padding or
 * casing alone — never admit a genuinely different IPA — which is the right
 * direction for a fail-closed gate (a padded CHAR column must not silently
 * present as "no claim access").
 */
export function filterAndSortClaimProviders(
  providers: ClaimProvider[],
  actingIpa: string,
): ClaimProvider[] {
  if (!actingIpa) return [];
  return providers
    .filter((p) => sameIpa(p.RmiCompanyId, actingIpa))
    .slice()
    .sort((a, b) =>
      claimProviderLabel(a).localeCompare(claimProviderLabel(b), undefined, {
        sensitivity: "base",
      }),
    );
}

/**
 * Legacy parity for `selectDefaultProvider` (helpers.ts:419, called at
 * CreateClaimModal:131-147): auto-select ONLY when the acting IPA yields
 * exactly one provider ("If there is exactly one match, then we will default to
 * the provider. Otherwise, we are going to let the provider choose" — legacy's
 * own comment). With two or more, there is no default and every control stays
 * disabled until the user picks, exactly as legacy does.
 *
 * The input is expected to be already filtered to the acting IPA (legacy
 * re-filtered inside the helper because it was handed the unfiltered array).
 */
export function selectDefaultClaimProvider(
  providers: ClaimProvider[],
): ClaimProvider | undefined {
  return providers.length === 1 ? providers[0] : undefined;
}

/** Stable identity for list keys / selection state (see `deduplicateProviders`
 *  upstream: a provider key alone is not unique across addresses). */
export function claimProviderKey(p: ClaimProvider): string {
  return `${p.RmiProvKeyId}_${p.RmiAddressId ?? ""}`;
}

/**
 * The identity of a gate DECISION: every input the gate actually decided on.
 *
 * The gate does not merely pick a provider for a member — it clears a
 * (member, health plan, tenant) TRIPLE:
 *   - `memberId` — who the claim is for;
 *   - `hpCode`   — the health plan the block rules (Caremore/NCPN, HNCC, BSPP)
 *                  were evaluated against, and the value the module forwards as
 *                  the required `hpCode` param;
 *   - `companyId`— the tenant the claim is written into, which is also what the
 *                  claim-write grant list was filtered by.
 *
 * Keying the resolved provider on the member ALONE is not enough. A MEMBER_BY_ID
 * refetch (any Apollo cache-and-network revalidation; AM-6260 removed the
 * `stableOnClaimCreated` → `refreshNow()` one) can return a different `ipaCode` under the same
 * `memberPolicyId`, e.g. when eligibility data lands. With a member-only key the
 * open form would keep running against rules evaluated for the OLD tenant, which
 * is precisely the silent-mis-routing these contractual rules exist to prevent.
 *
 * `hpCode` is supplied from the gate's RESOLVED Provider Portal row (the seed it
 * hands back through `onProceed`), not re-derived per render — it is the plan the
 * claim is actually filed under, and pairing it here is what stops the seed and
 * the provider from ever drifting apart. The two live values, `memberId` and
 * `companyId`, are what actually detect drift.
 *
 * Returns `""` — which `claimProviderStillValidForMember` never matches — when
 * the member cannot be identified at all: an unidentifiable member must not
 * inherit a previous member's clearance. (`toClaimMemberId` returns `""`, not
 * undefined, when it cannot resolve one.) Note this must be checked here rather
 * than left to the consumer: a naive template of three empty strings would be
 * `"||"`, which is truthy.
 *
 * `hpCode`/`companyId` are normalized `trim().toUpperCase()` to match how the
 * block rules (`checkClaimMemberAccess`) and the IPA filter (`sameIpa`) compare
 * them — so a padding- or casing-only difference in a refetched CHAR column,
 * which provably cannot change the gate's answer, does not needlessly close a
 * part-filled claim form.
 */
export function claimGateKey(
  memberId: string,
  hpCode: string | undefined,
  companyId: string | undefined,
): string {
  if (!memberId) return "";
  const norm = (v: string | undefined) => (v ?? "").trim().toUpperCase();
  return `${memberId}|${norm(hpCode)}|${norm(companyId)}`;
}

/**
 * A gate-resolved provider is only usable while the gate's decision still
 * applies — i.e. while the (member, health plan, tenant) triple it was granted
 * for is still the one on screen (see `claimGateKey`).
 *
 * In shell mode each patient tab is its own page instance (`key={tab.navId}`),
 * so the provider is per-member by construction. The CLASSIC route
 * (`App.tsx`'s `/members/:id`) mounts the profile page UNKEYED, and React Router
 * reuses that instance across `:id` changes — so a switch-patient there would
 * otherwise leave an open Create Claim form (and its provider) in place while
 * the member underneath changed. The provider itself is user-scoped, not
 * member-scoped, so that is not an authorization escape; the real hazard is that
 * the entry gate's health-plan block rules (Caremore/NCPN, HNCC, BSPP) are
 * evaluated once, at gate time, against the triple that was in scope THEN. Those
 * are contractual payer-routing rules — a claim silently started for a Blue
 * Shield PPO or Health Net Covered California member is exactly what they exist
 * to prevent.
 *
 * Pairing the provider with the decision it belongs to turns every such drift
 * into a plain "no provider resolved" state, which the caller's open condition
 * already fails closed on, sending the user back through the gate.
 *
 * A falsy `currentGateKey` never matches — see `claimGateKey`.
 */
export function claimProviderStillValidForMember(
  provider: ClaimProvider | null,
  resolvedForGateKey: string | null,
  currentGateKey: string,
): ClaimProvider | null {
  if (!provider || !currentGateKey) return null;
  return resolvedForGateKey === currentGateKey ? provider : null;
}
